// BookingForm.jsx
import React, { useContext, useEffect, useRef } from "react";
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  OutlinedInput,
  Select,
  MenuItem,
  Typography,
  Box,
  TextField,
  FormLabel,
  Stack,
} from "@mui/material";
import { StyledBox, StyledTypography } from "../../../../../platform/assets/css/styledComponent";
import { UserContext } from "../../../../../platform/components/auth/auth-context";
import SmsConsentCheckbox from "../../../../../platform/components/legal/SmsConsentCheckbox";

const BookingForm = ({
  questions,
  answers,
  errors,
  onAnswerChange,
  answerData,
  staff,
  bookingForMode,
  onBookingForModeChange,
  bookedForName,
  onBookedForNameChange,
  bookedForEmail,
  onBookedForEmailChange,
  bookedForPhone,
  onBookedForPhoneChange,
  additionalConfirmationEmails,
  onAdditionalConfirmationEmailsChange,
  sendCopyToBookedBy,
  onSendCopyToBookedByChange,
  smsConsent,
  onSmsConsentChange,
  smsConsentError,
}) => {
  const { providerUser } = useContext(UserContext);
  const inputRefs = useRef({});

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10;

    const syncAutofilledValues = () => {
      attempts += 1;
      Object.keys(inputRefs.current || {}).forEach((questionIdKey) => {
        const questionId = Number(questionIdKey);
        const node = inputRefs.current[questionId];
        const domValue = node?.value?.trim?.() || "";
        const currentValue = (answers?.[questionId]?.[0] || "").trim();
        if (domValue && domValue !== currentValue) {
          onAnswerChange(questionId, domValue, false);
        }
      });
    };

    const initialTimer = setTimeout(syncAutofilledValues, 100);
    const interval = setInterval(() => {
      syncAutofilledValues();
      if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 300);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [questions, answers, onAnswerChange]);

  const renderQuestionInput = (question) => {
    const title = question.title;
    const normalizedTitle = String(title || "").toLowerCase();
    const answerValue = answers[question.id] || [""];
    const errorForQuestion = errors[question.id];

    if (question.type.toLowerCase().includes("radio")) {
      return (
        <>
          <RadioGroup
            value={answerValue[0]}
            onChange={(e) => onAnswerChange(question.id, e.target.value, false)}
          >
            {answerData
              ?.getInviteeAnswers.filter((ans) => Number(ans.questionId) === Number(question.id))
              .map((ans) => (
                <FormControlLabel
                  key={ans.id}
                  value={ans.title}
                  control={<Radio />}
                  label={ans.title}
                />
              ))}
          </RadioGroup>
          {errorForQuestion && (
            <StyledBox className="error-message" sx={{ color: "error.main" }}>
              Please select an option.
            </StyledBox>
          )}
        </>
      );
    } else if (question.type.toLowerCase().includes("checkbox")) {
      return (
        <StyledBox>
          {answerData
            ?.getInviteeAnswers.filter((ans) => Number(ans.questionId) === Number(question.id))
            .map((ans) => (
              <FormControlLabel
                key={ans.id}
                value={ans.title}
                control={
                  <Checkbox
                    checked={(answers[question.id] || []).includes(ans.title)}
                    onChange={() => onAnswerChange(question.id, ans.title, true)}
                  />
                }
                label={ans.title}
              />
            ))}
          {errorForQuestion && (
            <StyledBox className="error-message" sx={{ color: "error.main" }}>
              Please select an option.
            </StyledBox>
          )}
        </StyledBox>
      );
    } else if (question.type.toLowerCase().includes("drop")) {
      return (
        <Select
          fullWidth
          value={answerValue[0]}
          onChange={(e) => onAnswerChange(question.id, e.target.value, false)}
          size="small"
          sx={{
            borderRadius: 2,
            backgroundColor: "background.default",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "divider",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "divider",
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                bgcolor: "background.paper",
                backgroundImage: "none",
                color: "text.primary",
                mt: 0.75,
                borderRadius: 2.5,
                border: 1, borderColor: "divider",
                boxShadow: "0 18px 36px rgba(15,31,46,0.12)",
              },
            },
          }}
        >
          {answerData
            ?.getInviteeAnswers.filter((ans) => Number(ans.questionId) === Number(question.id))
            .map((ans) => (
              <MenuItem key={ans.id} value={ans.title}>
                {ans.title}
              </MenuItem>
            ))}
        </Select>
      );
    } else {
      // Determine default value for email or name
      const defaultValue =
        normalizedTitle === "email"
          ? providerUser.email
          : normalizedTitle === "name"
          ? providerUser.username
          : "";
      const isOwnAccount = Number(providerUser.staffId) === Number(staff.staffId);

      const isDisabled =
            !isOwnAccount && (
              (normalizedTitle === "email" && providerUser.email) ||
          (normalizedTitle === "name" && providerUser.username)
        );


      return (
        <OutlinedInput
          onChange={(e) => onAnswerChange(question.id, e.target.value, false)}
          onBlur={(e) => onAnswerChange(question.id, e.target.value, false)}
          value={answerValue[0] || defaultValue}
          multiline={question.type.toLowerCase().includes("multi") || normalizedTitle === "notes"}
          minRows={normalizedTitle === "notes" ? 4 : undefined}
          variant="outlined"
          sx={{
            width: "100%",
            maxWidth: "100%",
            borderRadius: 2,
            backgroundColor: "background.default",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "divider",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "divider",
            },
          }}
          error={!!errorForQuestion}
          disabled={isDisabled}
          inputRef={(node) => {
            if (node) inputRefs.current[question.id] = node;
          }}
        />
      );
    }
  };

  return (
    <StyledBox
      sx={{
        flex: { xs: "1 1 100%", md: "1 1 64%" },
        minWidth: 0,
        padding: { xs: "20px", sm: "24px" },
        overflow: "auto",
        gap: "16px",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        border: 1, borderColor: "divider",
        bgcolor: "background.paper",
        color: "text.primary",
      }}
    >
      <Box sx={{ mb: 0.5 }}>
        <StyledTypography fontSize="22px" sx={{ fontWeight: 700, color: "text.primary" }}>
          Enter Your Details
        </StyledTypography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.7 }}>
          Fill in your details below. If you are booking for someone else, add their contact details below so confirmations go to the right person.
        </Typography>
      </Box>
      {questions?.map((question) => (
        <StyledBox
          key={question.id}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.85,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }}>
            {`${question.title} ${(question.required && question.title.toLowerCase() !== "email" && question.title.toLowerCase() !== "phone number") ? "*" : ""}`}
          </Typography>
          {renderQuestionInput(question)}
        </StyledBox>
      ))}
      <Box
        sx={{
          mt: 1,
          p: 2,
          borderRadius: 2.5,
          border: 1, borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <FormLabel sx={{ fontWeight: 700, color: "text.primary", mb: 1.25, display: "block" }}>
          Who is this booking for?
        </FormLabel>
        <RadioGroup
          value={bookingForMode}
          onChange={(e) => onBookingForModeChange?.(e.target.value)}
          sx={{ mb: bookingForMode === "other" ? 1.5 : 0.5 }}
        >
          <FormControlLabel value="self" control={<Radio />} label="For me" />
          <FormControlLabel
            value="other"
            control={<Radio />}
            label="For someone else"
          />
        </RadioGroup>
        {bookingForMode === "other" ? (
          <Stack spacing={1.5}>
            <TextField
              label="Attendee name"
              value={bookedForName}
              onChange={(e) => onBookedForNameChange?.(e.target.value)}
              fullWidth
            />
            <TextField
              label="Attendee email"
              value={bookedForEmail}
              onChange={(e) => onBookedForEmailChange?.(e.target.value)}
              fullWidth
            />
            <TextField
              label="Attendee phone"
              value={bookedForPhone}
              onChange={(e) => onBookedForPhoneChange?.(e.target.value)}
              fullWidth
            />
          </Stack>
        ) : null}
        <TextField
          label="Additional confirmation emails"
          value={additionalConfirmationEmails}
          onChange={(e) => onAdditionalConfirmationEmailsChange?.(e.target.value)}
          placeholder="name@example.com, second@example.com"
          helperText="Optional. Use commas to send confirmation to more people."
          fullWidth
          sx={{ mt: 1.5 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={sendCopyToBookedBy}
              onChange={(e) => onSendCopyToBookedByChange?.(e.target.checked)}
            />
          }
          label="Send a confirmation copy to me too"
          sx={{ mt: 0.5 }}
        />
        <SmsConsentCheckbox
          checked={smsConsent}
          onChange={onSmsConsentChange}
          error={smsConsentError}
          sx={{ mt: 1.25 }}
        />
      </Box>
    </StyledBox>
  );
};

export default BookingForm;
